import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { ApiResponse, PaginatedData, Page, PageListItem, CreatePageInput, UpdatePageInput, Version } from '@/types';

interface PageListParams {
  page?: number;
  perPage?: number;
  visibility?: 'public' | 'editors' | 'private';
  author?: number;
  tag?: string;
  sort?: 'title' | 'created_at' | 'updated_at';
  order?: 'asc' | 'desc';
}

// List pages
export function usePages(params: PageListParams = {}) {
  return useQuery({
    queryKey: ['pages', params],
    queryFn: async (): Promise<PaginatedData<PageListItem>> => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.perPage) searchParams.set('perPage', String(params.perPage));
      if (params.visibility) searchParams.set('visibility', params.visibility);
      if (params.author) searchParams.set('author', String(params.author));
      if (params.tag) searchParams.set('tag', params.tag);
      if (params.sort) searchParams.set('sort', params.sort);
      if (params.order) searchParams.set('order', params.order);

      const response = await api.get<ApiResponse<PaginatedData<PageListItem>>>(`/pages?${searchParams}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch pages');
      }
      return response.data.data;
    },
  });
}

// Get page by slug
export function usePage(slug: string) {
  return useQuery({
    queryKey: ['page', slug],
    queryFn: async (): Promise<Page> => {
      const response = await api.get<ApiResponse<Page>>(`/pages/${slug}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch page');
      }
      return response.data.data;
    },
    enabled: !!slug,
  });
}

// Get templates
export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: async (): Promise<PageListItem[]> => {
      const response = await api.get<ApiResponse<PageListItem[]>>('/pages/templates');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch templates');
      }
      return response.data.data;
    },
  });
}

// Get template by slug
export function useTemplate(slug: string) {
  return useQuery({
    queryKey: ['template', slug],
    queryFn: async (): Promise<Page> => {
      const response = await api.get<ApiResponse<Page>>(`/pages/${slug}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch template');
      }
      return response.data.data;
    },
    enabled: !!slug,
  });
}

// Create page
export function useCreatePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePageInput): Promise<Page> => {
      const response = await api.post<ApiResponse<Page>>('/pages', data);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to create page');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });
}

// Update page
export function useUpdatePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, data }: { slug: string; data: UpdatePageInput }): Promise<Page> => {
      const response = await api.put<ApiResponse<Page>>(`/pages/${slug}`, data);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to update page');
      }
      return response.data.data;
    },
    onSuccess: (_, { slug }) => {
      queryClient.invalidateQueries({ queryKey: ['page', slug] });
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });
}

// Delete page
export function useDeletePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await api.delete<ApiResponse>(`/pages/${slug}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to delete page');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });
}

// Update visibility
export function useUpdateVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, visibility }: { slug: string; visibility: 'public' | 'editors' | 'private' }) => {
      const response = await api.patch<ApiResponse>(`/pages/${slug}/visibility`, { visibility });
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to update visibility');
      }
      return response.data;
    },
    onSuccess: (_, { slug }) => {
      queryClient.invalidateQueries({ queryKey: ['page', slug] });
    },
  });
}

// Get page versions
export function usePageVersions(slug: string, params: { page?: number; perPage?: number } = {}) {
  return useQuery({
    queryKey: ['pageVersions', slug, params],
    queryFn: async (): Promise<PaginatedData<Version>> => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.perPage) searchParams.set('perPage', String(params.perPage));

      const response = await api.get<ApiResponse<PaginatedData<Version>>>(`/pages/${slug}/versions?${searchParams}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch versions');
      }
      return response.data.data;
    },
    enabled: !!slug,
  });
}

// Restore version
export function useRestoreVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, version }: { slug: string; version: number }) => {
      const response = await api.post<ApiResponse<Version>>(`/pages/${slug}/versions/${version}/restore`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to restore version');
      }
      return response.data.data;
    },
    onSuccess: (_, { slug }) => {
      queryClient.invalidateQueries({ queryKey: ['page', slug] });
      queryClient.invalidateQueries({ queryKey: ['pageVersions', slug] });
    },
  });
}

// Get version diff (compare two versions)
export function useVersionDiff(slug: string, v1: number | null, v2: number | null) {
  return useQuery({
    queryKey: ['versionDiff', slug, v1, v2],
    queryFn: async (): Promise<{ v1: Version; v2: Version } | null> => {
      if (!v1 || !v2) return null;
      const response = await api.get<ApiResponse<{ v1: Version; v2: Version }>>(`/pages/${slug}/versions/${v1}/${v2}/diff`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch version diff');
      }
      return response.data.data;
    },
    enabled: !!slug && !!v1 && !!v2,
  });
}

// Copy/clone page
export function useCopyPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string): Promise<Page> => {
      const response = await api.post<ApiResponse<Page>>(`/pages/${slug}/copy`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to copy page');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });
}
